import { ArrowLeft } from "lucide-react";
import logo from "../public/logo.png";

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      {/* Nav */}
      <nav className="flex items-center justify-between px-5 md:px-10 lg:px-16 py-5 border-b border-white/10">
        <a href="/">
          <img src={logo} alt="Empire Insurance" className="h-10 md:h-12" />
        </a>
        <a
          href="/"
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </a>
      </nav>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-12 md:py-20">
        <h1 className="text-3xl md:text-4xl font-extrabold mb-2">Privacy Policy</h1>
        <p className="text-gray-500 mb-10">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <div className="prose prose-invert prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-bold text-white mb-4">1. Introduction</h2>
            <p className="text-gray-400 leading-relaxed">
              Empire Insurance Agency ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or submit an application through our platform.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-4">2. Information We Collect</h2>
            <p className="text-gray-400 leading-relaxed mb-4">We collect information that you provide directly to us, including:</p>
            <ul className="list-disc list-inside text-gray-400 space-y-2">
              <li>Personal identifiers (name, email address, phone number)</li>
              <li>Location information (city, state, county)</li>
              <li>Professional information (sales experience, licensing status)</li>
              <li>Employment preferences and qualifications</li>
              <li>Any other information you choose to provide in your application</li>
            </ul>
            <p className="text-gray-400 leading-relaxed mt-4">We also automatically collect certain information when you visit our website:</p>
            <ul className="list-disc list-inside text-gray-400 space-y-2">
              <li>Device and browser information</li>
              <li>IP address and location data</li>
              <li>Pages visited and time spent on our site</li>
              <li>Referring URLs and search terms</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-400 leading-relaxed mb-4">We use the information we collect to:</p>
            <ul className="list-disc list-inside text-gray-400 space-y-2">
              <li>Process and evaluate your employment application</li>
              <li>Contact you regarding career opportunities</li>
              <li>Communicate with you about our services and updates</li>
              <li>Improve our website and recruitment process</li>
              <li>Comply with legal obligations</li>
              <li>Protect against fraudulent or unauthorized activity</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-4">4. Information Sharing</h2>
            <p className="text-gray-400 leading-relaxed mb-4">We may share your information with:</p>
            <ul className="list-disc list-inside text-gray-400 space-y-2">
              <li>Service providers who assist in our operations (e.g., email services, data storage)</li>
              <li>Insurance carriers and partners for employment verification</li>
              <li>Legal authorities when required by law</li>
              <li>Professional advisors (lawyers, accountants) as necessary</li>
            </ul>
            <p className="text-gray-400 leading-relaxed mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-4">5. Data Security</h2>
            <p className="text-gray-400 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-4">6. Your Rights</h2>
            <p className="text-gray-400 leading-relaxed mb-4">Depending on your location, you may have the right to:</p>
            <ul className="list-disc list-inside text-gray-400 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt-out of marketing communications</li>
              <li>Withdraw consent where applicable</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-4">7. Cookies and Tracking</h2>
            <p className="text-gray-400 leading-relaxed">
              We use cookies and similar tracking technologies to improve your experience on our website, analyze traffic, and for marketing purposes. You can control cookies through your browser settings, though disabling them may affect site functionality.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-4">8. Third-Party Links</h2>
            <p className="text-gray-400 leading-relaxed">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites and encourage you to review their privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-4">9. Children's Privacy</h2>
            <p className="text-gray-400 leading-relaxed">
              Our website is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children under 18.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-4">10. Changes to This Policy</h2>
            <p className="text-gray-400 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-4">11. Contact Us</h2>
            <p className="text-gray-400 leading-relaxed">
              If you have questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="text-gray-400 mt-4">
              <strong className="text-white">Empire Insurance Agency</strong><br />
              South Florida<br />
              Email: admin@getempire.io
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="px-5 md:px-10 lg:px-16 py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm text-gray-600">
            © {new Date().getFullYear()} Empire Insurance Agency. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
